import { sortItemsInNestedArchive } from '../../../ts/helper';
import { lazyLoading } from '../../../ts/lazyLoading';
import { smoothScrollToElement } from '../../../ts/smoothScrolling';

/**
 * Friendation component
 * @param component Instance of this _Friendation_ component
 */
const blockArchiveBlogFunction = async (component: HTMLElement) => {
  /**
   * Message elements that change their visibility based on the state of the {@link component}
   */
  const messageElements = {
    /** X results message container */
    results: <HTMLElement>component.querySelector('.message--results'),

    /** No results message container */
    noResults: <HTMLElement>component.querySelector('.message--noResults'),

    /** Loading message container */
    loading: <HTMLElement>component.querySelector('.message--loading')
  };

  /**
   * All form elements
   */
  const formElements = {
    /** Search form */
    form: component.querySelector('form'),

    /** Wrapper for all filters and sorting function. Hidden by default. Will be visible when a search term is present. */
    filterWrapper: <HTMLElement>component.querySelector('.filters'),

    /** The search input that the user is typing in */
    searchInput: <HTMLInputElement>(
      component.querySelector('input[type="search"]')
    ),

    /** Button that will reset the form */
    resetButton: <HTMLButtonElement>component.querySelector('[type="reset"]'),

    /** Submit button in {@link form} */
    submitButton: <HTMLButtonElement>component.querySelector('form button'),

    /** A select element that lets the user pick a sort method */
    sortByDate: <HTMLSelectElement>(
      component.querySelector('[name="sortByDate"]')
    ),

    /** A select element that lets the user pick a sort method */
    sortByCategory: <HTMLSelectElement>(
      component.querySelector('[name="sortByCategory"]')
    )
  };

  /** Container with results that contains the {@link searchResultItems} */
  const resultsList: HTMLUListElement = component.querySelector(
    '.searchResults-list'
  );

  /** Get the search result items that are currently in the `DOM` */
  const searchResultItems = () =>
    <HTMLElement[]>(
      Array.from(resultsList.querySelectorAll('.searchResults-list > li'))
    );

  /**
   * The submit event on {@link formElements.form} will trigger a search
   */
  const onSearch = () => {
    /**
     * On {@link formElements.form} reset
     */
    const onReset = () => {
      formElements.form.setAttribute('aria-busy', 'true');
      const url = new URL(window.location.href);
      url.search = '';
      window.history.pushState({}, '', url.href);
      window.location.reload();

      formElements.resetButton.hidden = true;
    };

    /**
     * On {@link formElements.form} submit
     */
    const onSubmit = (e: SubmitEvent) => {
      e.preventDefault();

      /** The new term that the user wants to search */
      const newTerm = formElements.searchInput.value.trim();

      /** Current url */
      const url = new URL(window.location.href);

      /** Current old search term from the {@link url} that we can compare with the {@link newTerm} */
      const oldTerm = url.searchParams.get('q')?.trim();

      if (oldTerm && !newTerm) {
        onReset();
        return;
      }

      formElements.resetButton.hidden = false;

      /** Avoid submission of the same term */
      if (oldTerm && newTerm === oldTerm) {
        smoothScrollToElement(resultsList);

        return;
      }

      /** Avoid multiple form submits */
      if (formElements.form.hasAttribute('aria-busy')) return;
      formElements.form.setAttribute('aria-busy', 'true');
      if (formElements.submitButton) formElements.submitButton.disabled = true;

      /** Hide all messages */
      messageElements.results.inert = true;
      messageElements.noResults.inert = true;

      /** Show a loading message */
      messageElements.loading.inert = false;

      /** Update the search parameter value in the {@link url} */
      url.searchParams.set('q', newTerm);

      /** Update the URL in browsers address bar */
      window.history.pushState(
        {
          url: url.toString()
        },
        '',
        url.toString()
      );

      /** Fetch results with the {@link newTerm} */
      fetch(url.href, {})
        .then((x) => x.text())
        .then((x) => {
          const tempDom = document.createElement('div');
          tempDom.innerHTML = x;

          /** Search result items */
          const items = Array.from(
            tempDom.querySelectorAll('.searchResults-list > li')
          );

          /** Hide loading message */
          messageElements.loading.inert = true;

          if (items.length) {
            /** Show the „We found X results” message */
            messageElements.results.inert = false;
          } else {
            /** Show the „We found nothing” message */
            messageElements.noResults.inert = false;
          }

          /** Clear old items */
          resultsList.innerHTML = '';

          /** Append new items */
          items.forEach((item) => {
            /** Trigger animation */
            item.classList.add('animation--started');
            resultsList.append(item);
          });

          /** Sort {@link resultsList} */
          handleSortByDate();

          /** Filter {@link resultsList} */
          handleFilterByCategory();
        })
        .then(() => {
          /** Update the „We found X results” message */
          updateSearchResultsCount();

          setTimeout(() => {
            if (resultsList.childNodes.length) {
              // any results found
              resultsList.inert = false;
              smoothScrollToElement(resultsList);
              lazyLoading();
            } else {
              // no results found
              resultsList.inert = true;
              smoothScrollToElement(messageElements.noResults);
            }

            /** Make form accessible again */
            formElements.form.removeAttribute('aria-busy');
            if (formElements.submitButton)
              formElements.submitButton.disabled = false;

            /** Display filters */
            formElements.filterWrapper.inert = false;
          }, 100);
        });
    };

    formElements.form.addEventListener('reset', onReset.bind(this));
    formElements.form.addEventListener('submit', onSubmit.bind(this));
  };

  /**
   * Update the results count in the {@link messageElements.results} element
   * @returns
   */
  const updateSearchResultsCount = () => {
    if (!messageElements.results) return;

    /**
     * Text that contains a placeholder
     * @example
     *   "Found %results% results"
     */
    const text = messageElements.results.dataset.text;

    /** Results found */
    const amount = resultsList.querySelectorAll(
      ':scope > li:not([inert])'
    ).length;

    // update text
    messageElements.results.innerHTML = `<h2>${text
      .replace('%results%', amount.toString())
      .trim()}</h2>`;
  };

  /** Sort {@link resultsList} */
  const handleSortByDate = () => {
    if (!formElements.sortByDate) return;

    /** Update the view depending on the sort {@link direction} */
    const update = () => {
      const direction = <'ascending' | 'descending'>(
        formElements.sortByDate.value
      );

      sortItemsInNestedArchive(searchResultItems(), direction, 'date');
    };

    if (!formElements.sortByDate.hasAttribute('listening')) {
      formElements.sortByDate.addEventListener('change', update);
      formElements.sortByDate.setAttribute('listening', 'true');
    }

    update();
  };

  /**
   * Filter {@link resultsList}
   */
  const handleFilterByCategory = () => {
    if (!formElements.sortByCategory) return;

    /** Update the view depending on the picked {@link category} */
    const update = () => {
      const allValue = '*';
      const category = <typeof allValue | string>(
        formElements.sortByCategory.value
      );

      if (category === allValue) {
        /** Show all */
        searchResultItems().forEach((x) => {
          x.inert = false;
        });
      } else {
        searchResultItems().forEach((x) => {
          x.inert = !x
            .getAttribute('data-category')
            .trim()
            .split(',')
            .includes(category.trim());
        });
      }

      updateSearchResultsCount();
    };

    if (!formElements.sortByCategory.hasAttribute('listening')) {
      formElements.sortByCategory.addEventListener('change', update);
      formElements.sortByCategory.setAttribute('listening', 'true');
    }
    update();
  };

  /**
   * Show the {@link resultsList} when any results are found
   */
  const updateVisibilityOnLoad = () => {
    if (searchResultItems().length) {
      /** Hide {@link resultsList} */
      resultsList.inert = false;

      /** Display filters */
      formElements.filterWrapper.inert = false;
    }
  };

  /** Update messages */
  updateSearchResultsCount();

  /** Toggle elements */
  updateVisibilityOnLoad();

  /** Listen for search input */
  onSearch();

  /** Sort {@link resultsList} */
  handleSortByDate();

  /** Filter {@link resultsList} */
  handleFilterByCategory();
};

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="block-archive-blog"]';

/** Function that handles a single instance of this _Friendation_ component */
const classFunction: (component: HTMLElement) => void =
  blockArchiveBlogFunction;

/**
 * Friendation component
 */
export const blockArchiveBlog = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((x) => {
    classFunction.call(this, x);
  });
};
